/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * SearchAutocomplete — Barre de recherche avec suggestions à la Google.
 * Affiche :
 *  • Les recherches récentes (localStorage) quand le champ est vide et focalisé.
 *  • Les recherches fréquentes prédéfinies quand le champ est vide.
 *  • Les suggestions filtrées (titres de documents + requêtes populaires) en cours de frappe.
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Search, Clock, TrendingUp, ArrowUpLeft, X } from 'lucide-react';
import { LegalDocument } from '../../types';
import { searchService } from '../../Services';

// ─── Requêtes juridiques populaires ─────────────────────────────────────────
const POPULAR_QUERIES: string[] = [];

const HISTORY_KEY = 'docarn_search_history';
const MAX_HISTORY = 8;

// ─── Types ───────────────────────────────────────────────────────────────────
interface SearchAutocompleteProps {
  /** Valeur courante du champ (contrôlé depuis l'extérieur) */
  value: string;
  /** Callback appelé quand la valeur change (frappe) */
  onChange: (value: string) => void;
  /** Callback appelé quand une recherche est soumise (Entrée ou clic suggestion) */
  onSearch: (term: string) => void;
  /** Base de documents pour les suggestions live */
  documents?: LegalDocument[];
  /** Placeholder du champ */
  placeholder?: string;
  /** Variante visuelle : 'hero' (grande barre d'accueil) | 'header' (petite barre du header) | 'search' (page recherche) */
  variant?: 'hero' | 'header' | 'search';
  /** Classe CSS additionnelle sur le wrapper */
  className?: string;
  /** Id unique (pour les labels d'accessibilité) */
  id?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function loadHistory(): string[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveHistory(history: string[]) {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, MAX_HISTORY)));
  } catch { /* ignore */ }
}

function pushToHistory(term: string) {
  const history = loadHistory().filter(h => h.toLowerCase() !== term.toLowerCase());
  saveHistory([term, ...history]);
}

function removeFromHistory(term: string) {
  saveHistory(loadHistory().filter(h => h !== term));
}

function HighlightMatch({ text, query, highlightHtml }: { text: string; query: string; highlightHtml?: string }) {
  const MAX_LEN = 120; // Number of characters for the snippet

  // 1. Parse into segments
  let segments: { text: string; isMatch: boolean }[] = [];
  if (highlightHtml) {
    const parts = highlightHtml.split(/(<strong>.*?<\/strong>)/gi);
    segments = parts.map(part => {
      if (part.toLowerCase().startsWith('<strong>')) {
        return { text: part.substring(8, part.length - 9), isMatch: true };
      }
      return { text: part, isMatch: false };
    });
  } else if (query.trim()) {
    const escaped = query.trim().replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
    segments = parts.map(part => ({
      text: part,
      isMatch: part.toLowerCase() === query.toLowerCase().trim()
    }));
  } else {
    segments = [{ text, isMatch: false }];
  }

  // 2. Generate Snippet
  const totalLen = segments.reduce((acc, s) => acc + s.text.length, 0);
  let finalSegments = segments;

  if (totalLen > MAX_LEN) {
    const firstMatchIdx = segments.findIndex(s => s.isMatch);
    if (firstMatchIdx === -1) {
      finalSegments = [{ text: text.substring(0, MAX_LEN) + '...', isMatch: false }];
    } else {
      let matchStartPos = 0;
      for (let i = 0; i < firstMatchIdx; i++) {
        matchStartPos += segments[i].text.length;
      }
      const matchLen = segments[firstMatchIdx].text.length;

      const halfWindow = Math.floor((MAX_LEN - matchLen) / 2);
      let windowStart = Math.max(0, matchStartPos - halfWindow);
      let windowEnd = windowStart + MAX_LEN;

      if (windowEnd > totalLen) {
        windowEnd = totalLen;
        windowStart = Math.max(0, windowEnd - MAX_LEN);
      }

      // Snap to words
      const plainText = segments.map(s => s.text).join('');
      if (windowStart > 0) {
        const space = plainText.indexOf(' ', windowStart);
        if (space !== -1 && space < matchStartPos) windowStart = space + 1;
      }
      if (windowEnd < totalLen) {
        const space = plainText.lastIndexOf(' ', windowEnd);
        if (space !== -1 && space > matchStartPos + matchLen) windowEnd = space;
      }

      const result: { text: string; isMatch: boolean }[] = [];
      if (windowStart > 0) result.push({ text: '...', isMatch: false });

      let pos = 0;
      for (const seg of segments) {
        const segStart = pos;
        const segEnd = pos + seg.text.length;
        if (segEnd > windowStart && segStart < windowEnd) {
          let startInSeg = Math.max(0, windowStart - segStart);
          let endInSeg = Math.min(seg.text.length, windowEnd - segStart);
          result.push({ text: seg.text.substring(startInSeg, endInSeg), isMatch: seg.isMatch });
        }
        pos += seg.text.length;
      }

      if (windowEnd < totalLen) result.push({ text: '...', isMatch: false });
      finalSegments = result;
    }
  }

  // 3. Render segments
  return (
    <span title={text}>
      {finalSegments.map((seg, i) =>
        seg.isMatch
          ? <strong key={i} className="font-extrabold text-zinc-900 bg-yellow-100 rounded-sm px-0.5">{seg.text}</strong>
          : <span key={i} className="text-zinc-500">{seg.text}</span>
      )}
    </span>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────
export const SearchAutocomplete: React.FC<SearchAutocompleteProps> = ({
  value,
  onChange,
  onSearch,
  documents = [],
  placeholder = 'Rechercher un texte de loi...',
  variant = 'search',
  className = '',
  id = 'search-autocomplete',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [history, setHistory] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [apiSuggestions, setApiSuggestions] = useState<{ label: string; highlight?: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch autocompletion suggestions from the backend API with debounce
  useEffect(() => {
    const q = value.trim();
    if (!q) {
      setApiSuggestions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const abortController = new AbortController();

    const handler = setTimeout(async () => {
      try {
        const results = await searchService.autocomplete(q, 5, abortController.signal);
        setApiSuggestions(results.map(r => ({
          label: r.officialTitle,
          highlight: r.highlight,
        })));
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          console.error('Error fetching autocomplete suggestions:', err);
        }
      } finally {
        setIsLoading(false);
      }
    }, 250);

    return () => {
      clearTimeout(handler);
      abortController.abort();
    };
  }, [value]);

  // Reload history when dropdown opens
  useEffect(() => {
    if (isOpen) setHistory(loadHistory());
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ─── Build suggestion list ──────────────────────────────────────────────
  const suggestions = React.useMemo(() => {
    const q = value.trim().toLowerCase();

    if (!q) {
      // Empty field: show recent history + popular picks
      const recentItems = history.map(h => ({ label: h, type: 'history' as const }));
      const popularItems = POPULAR_QUERIES
        .filter(p => !history.some(h => h.toLowerCase() === p.toLowerCase()))
        .slice(0, Math.max(0, 8 - history.length))
        .map(p => ({ label: p, type: 'popular' as const }));
      return [...recentItems, ...popularItems];
    }

    // Typing: suggestions from the API only
    const docSuggestions = apiSuggestions.map(item => ({
      label: item.label,
      type: 'document' as const,
      highlight: item.highlight
    }));

    const popularSuggestions = POPULAR_QUERIES
      .filter(p =>
        p.toLowerCase().includes(q) &&
        !docSuggestions.some(d => d.label.toLowerCase() === p.toLowerCase())
      )
      .slice(0, 4)
      .map(p => ({ label: p, type: 'popular' as const }));

    const historySuggestions = history
      .filter(h =>
        h.toLowerCase().includes(q) &&
        !docSuggestions.some(d => d.label.toLowerCase() === h.toLowerCase())
      )
      .slice(0, 3)
      .map(h => ({ label: h, type: 'history' as const }));

    return [...historySuggestions, ...popularSuggestions, ...docSuggestions];
  }, [value, history, apiSuggestions]);

  // ─── Keyboard navigation ────────────────────────────────────────────────
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
        handleSelectSuggestion(suggestions[highlightedIndex].label);
      } else if (value.trim()) {
        handleSubmit(value.trim());
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  const handleSelectSuggestion = useCallback((label: string) => {
    onChange(label);
    searchService.trackSearch({ query: label, searchOrigin: 'AUTOCOMPLETE_CLICK', totalResults: 1 });
    handleSubmit(label);
  }, [onChange]);

  const handleSubmit = (term: string) => {
    if (!term.trim()) return;
    pushToHistory(term.trim());
    setHistory(loadHistory());
    setIsOpen(false);
    setHighlightedIndex(-1);
    onSearch(term.trim());
  };

  const handleDeleteHistory = (e: React.MouseEvent, term: string) => {
    e.stopPropagation();
    removeFromHistory(term);
    setHistory(loadHistory());
  };

  // ─── Styles by variant ──────────────────────────────────────────────────
  const inputStyles = {
    hero: 'w-full text-base sm:text-lg font-medium py-5 sm:py-6 pl-14 pr-14 bg-transparent text-zinc-900 border-0 focus:ring-0 focus:outline-none placeholder-zinc-400',
    search: 'w-full text-sm sm:text-[15px] font-normal py-3 pl-12 pr-11 bg-transparent text-zinc-900 border-0 focus:ring-0 focus:outline-none placeholder-zinc-400',
    header: 'w-full pl-9 pr-8 py-1.5 text-xs bg-zinc-50 border border-zinc-200 rounded-full focus:outline-none focus:bg-white focus:border-violet-medium transition-all',
  };

  const wrapperStyles = {
    hero: 'bg-white shadow-2xl shadow-zinc-300/50 rounded-2xl relative border border-zinc-200',
    search: 'bg-white shadow-[0_1px_6px_rgba(32,33,36,0.14)] hover:shadow-[0_2px_8px_rgba(32,33,36,0.20)] focus-within:shadow-[0_2px_8px_rgba(32,33,36,0.24)] rounded-full relative border border-zinc-200/80 transition-all duration-200',
    header: 'relative',
  };

  const iconSize = variant === 'header' ? 'w-3.5 h-3.5' : variant === 'search' ? 'w-4 h-4' : 'w-6 h-6';
  const iconPos  = variant === 'header' ? 'left-3 top-2' : variant === 'search' ? 'left-4 top-3.5' : 'left-5 top-5';

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* ── Input wrapper ───────────────────────────────────────────────── */}
      <div className={`${wrapperStyles[variant]} ${isOpen && suggestions.length > 0 && variant === 'hero' ? 'rounded-b-none border-b-0' : ''}`}>
        {/* Search icon */}
        <Search className={`${iconSize} text-zinc-400 absolute ${iconPos} pointer-events-none z-10`} />

        <input
          ref={inputRef}
          id={id}
          type="text"
          autoComplete="off"
          placeholder={placeholder}
          value={value}
          onChange={e => { onChange(e.target.value); setIsOpen(true); setHighlightedIndex(-1); }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          className={inputStyles[variant]}
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={`${id}-listbox`}
        />

        {/* Clear button */}
        {value && variant !== 'header' && (
          <button
            type="button"
            onClick={() => { onChange(''); inputRef.current?.focus(); }}
            className={`absolute ${variant === 'search' ? 'right-4' : 'right-5'} top-1/2 -translate-y-1/2 text-zinc-300 hover:text-zinc-600 transition-colors cursor-pointer`}
          >
            <X className={variant === 'search' ? 'w-4 h-4' : 'w-5 h-5'} />
          </button>
        )}
      </div>

      {/* ── Dropdown ─────────────────────────────────────────────────────── */}
      {isOpen && suggestions.length > 0 && (
        <div
          id={`${id}-listbox`}
          role="listbox"
          className={`absolute left-0 right-0 z-50 bg-white border border-zinc-200 shadow-2xl shadow-zinc-300/30 overflow-hidden animate-fade-in ${
            variant === 'header'
              ? 'rounded-2xl mt-1 border-t border-zinc-200'
              : variant === 'search'
              ? 'rounded-2xl mt-1.5 shadow-[0_4px_18px_rgba(32,33,36,0.18)] border border-zinc-200/90'
              : 'rounded-b-2xl border-t-0'
          }`}
        >
          {/* Section labels */}
          {!value.trim() && (
            <div className="px-4 pt-3 pb-1 flex items-center justify-between">
              <span className="text-[10px] font-extrabold uppercase tracking-widest text-zinc-400">
                {history.length > 0 ? 'Recherches récentes & tendances' : 'Recherches fréquentes'}
              </span>
              {history.length > 0 && (
                <button
                  onClick={() => { saveHistory([]); setHistory([]); }}
                  className="text-[10px] font-bold text-zinc-400 hover:text-red-500 transition-colors cursor-pointer"
                >
                  Tout effacer
                </button>
              )}
            </div>
          )}

          {value.trim() && (
            <div className="px-4 pt-3 pb-1">
              <span className="text-[10px] font-extrabold uppercase tracking-widest text-zinc-400">
                Suggestions
              </span>
            </div>
          )}

          <ul className={`divide-y divide-zinc-50 ${variant === 'header' ? 'max-h-64' : 'max-h-80'} overflow-y-auto`}>
            {suggestions.map((item, index) => {
              const isHighlighted = index === highlightedIndex;
              const Icon = item.type === 'history' ? Clock : item.type === 'popular' ? TrendingUp : Search;
              const iconColor = item.type === 'history' ? 'text-zinc-400' : item.type === 'popular' ? 'text-primary-500' : 'text-zinc-400';

              return (
                <li
                  key={`${item.type}-${index}`}
                  role="option"
                  aria-selected={isHighlighted}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onMouseLeave={() => setHighlightedIndex(-1)}
                  onClick={() => handleSelectSuggestion(item.label)}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                    isHighlighted ? 'bg-primary-50' : 'hover:bg-zinc-50'
                  }`}
                >
                  {/* Left icon */}
                  <Icon className={`w-4 h-4 shrink-0 ${iconColor}`} />

                  {/* Label with highlight */}
                  <span className={`flex-1 text-sm leading-snug break-words whitespace-normal ${variant === 'header' ? 'text-xs' : 'text-sm'}`}>
                    <HighlightMatch text={item.label} query={value} highlightHtml={(item as any).highlight} />
                  </span>

                  {/* Right-side actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    {/* Delete from history */}
                    {item.type === 'history' && (
                      <button
                        onClick={(e) => handleDeleteHistory(e, item.label)}
                        className="p-1 text-zinc-300 hover:text-red-400 transition-colors rounded-full hover:bg-red-50"
                        title="Supprimer"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                    {/* Arrow to fill the input (like Google ↖) */}
                    <button
                      onClick={(e) => { e.stopPropagation(); onChange(item.label); inputRef.current?.focus(); }}
                      className="p-1 text-zinc-300 hover:text-zinc-600 transition-colors rounded-full hover:bg-zinc-100"
                      title="Compléter"
                    >
                      <ArrowUpLeft className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>

          {/* Submit current typed query */}
          {value.trim() && (
            <div className="border-t border-zinc-100 px-4 py-3">
              <button
                onClick={() => handleSubmit(value.trim())}
                className="w-full flex items-center gap-3 text-sm font-bold text-primary-750 hover:text-primary-800 transition-colors"
              >
                <Search className="w-4 h-4 shrink-0" />
                <span>Rechercher <strong>"{value}"</strong></span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
